import React, { FunctionComponent, useEffect, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { fetchAll, fetchOne } from '../../lib/v31lib';

import {
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';

import { loginAtom } from '../../lib/auth';

import { AlertCheckInputs } from '../../components/alerts/CheckInputs';
import { AlertSaved } from '../../components/alerts/Saved';
import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import {
  Page,
  StyledButton,
  StyledSelect,
  StyledTextField,
  Typography,
} from '../../components/interface';

import {
  Investment,
  InvestmentManager
} from '../../models';

// const { DateTime } = require("luxon");

type Props = {};

export const InvestmentDetail: FunctionComponent<Props> = () => {
  const navigate = useNavigate();
  // const theme = useTheme();
  // const { isLoading, isError, data, error } = useQuery({
  //   queryKey: ['login'],
  //   queryFn: async () => {return },
  // });

  const params = useParams();

  const [login, setLogin] = useAtom(loginAtom);
  const [editableObject, setEditableObject] = useState<Investment|null>(null);
  const [objectId, setObjectId] = useState<string | undefined>(params.id);

  const [errorSnackOpen, setErrorSnackOpen] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);

  const investmentTypes:{[z:string]: string} = {
    'bonds': "Bonds",
    'real_estate': "Real Estate",
    'obligations': "Obligations",
    'equities': "Equities",
    'gold': "Gold & other commodities"
  };

  const fields = [
    'id', 'created_at', 'updated_at', 'name', 'isin', 'amount', 'original_purchase_price', 'currency', 'ft_override',
    'custom_url', 'show_url', 'investment_manager_id', 'investment_type'
  ];

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["investment", objectId],
    queryFn: () =>
      fetchOne<Investment>(
        `/nl/v3/objects/fetch_all`,
        {
          object: 'investment',
          id: objectId,
          fields: fields
        },
        login
      ),
    // select: (d) => {
    //   return {
    //     ...d,
    //     start_date: DateTime.fromISO(d.start_date).toFormat("dd/LL/y"),
    //     end_date: DateTime.fromISO(d.end_date).toFormat("dd/LL/y"),
    //   };
    // },
    enabled: !!login && parseInt(objectId || '', 10) > 0,
  });

  const {
    data: investmentManagers,
    // isLoading: objectsLoading,
    // isError: adviceUploadsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["investment_managers"],
    queryFn: () =>
      fetchAll<InvestmentManager>(
        `/nl/v3/objects/fetch_all`,
        {
          object: 'investment_manager',
          fields: ['id', 'created_at', 'name'],
          per_page: 200,
          page: 0,
          order: "name ASC",
          filter: {
            advanced: {
              placeholder: 0
            }
          }
        },
        login
      )
  });
  









  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean
    }) => {
      return fetchOne<Investment>(
        `/nl/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setSavedOpen(true);
      if (!variables.keepOpen) navigate("/internal/investments");
      if (!!variables.keepOpen && objectId === 'new') navigate(`/internal/investment/${data.id}`);
      if (!!variables.keepOpen && objectId !== 'new') objectRefetch();
    },
  });











  useEffect(() => {
    if (!!object) setEditableObject(object);
  }, [object]);

  return (
    <Page sx={{}} title="Investment Detail">
      <Grid container spacing="8">
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{
            textDecoration: 'underline'
          }}>Base details</Typography>
        </Grid>
        <Grid item xs={12}>
          <StyledSelect
            label="Manager"
            value={editableObject?.investment_manager_id?.toString() || ''}
            id="investment_manager_id"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                investment_manager_id: !!v ? parseInt(v, 10) : undefined
              });
            }}
            list={[
              {id: '', name: "--- ---"},
              ...(investmentManagers || []).map(mngr => {
                return {id: mngr.id?.toString() || '', name: mngr.name || ''};
              })
            ]}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="ISIN"
            value={editableObject?.isin || ''}
            id="isin"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                isin: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Name"
            value={editableObject?.name || ''}
            id="name"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                name: v
              });
            }}
            fullWidth
          />
        </Grid>



        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{
            textDecoration: 'underline'
          }}>Initial acquirement details</Typography>
        </Grid>
        <Grid item xs={5}>
          <StyledTextField
            label="Amount"
            value={editableObject?.amount?.toString() || ''}
            id="name"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                amount: !!v ? parseInt(v.replace(",", "."), 10) : undefined
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={5}>
          <StyledTextField
            label="Original purchase price"
            value={editableObject?.original_purchase_price?.toString() || ''}
            id="original_purchase_price"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                original_purchase_price: !!v ? parseFloat(v.replace(",", ".")) : undefined
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <StyledSelect
            label="Currency"
            value={editableObject?.currency || 'EUR'}
            id="currency"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                currency: v
              });
            }}
            list={[
              {id: 'EUR', name: "EUR"},
              {id: 'USD', name: "USD"}
            ]}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledSelect
            label="Investment Type"
            value={editableObject?.investment_type || 'funds'}
            id="investment_type"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                investment_type: v
              });
            }}
            list={Object.keys(investmentTypes).sort().map(invKey => {
              return {id: invKey, name: investmentTypes[invKey]};
            })}
          />
        </Grid>



        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{
            textDecoration: 'underline'
          }}>Scrape details</Typography>
        </Grid>
        <Grid item xs={6}>
          <StyledSelect
            label="URL Type"
            value={editableObject?.scrape_type || 'funds'}
            id="scrape_type"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                scrape_type: v
              });
            }}
            list={[
              {id: 'bonds', name: "ETF"},
              {id: 'funds', name: "Funds"}
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            label="FT Override"
            value={editableObject?.ft_override || ''}
            id="ft_override"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                ft_override: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            label="Custom URL"
            value={editableObject?.custom_url || ''}
            id="custom_url"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                custom_url: v
              });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            label="Show URL"
            value={editableObject?.show_url || ''}
            id="show_url"
            onChange={(v) => {
              setEditableObject({
                ...(editableObject || {
                  name: '',
                  isin: ''
                }),
                show_url: v
              });
            }}
            fullWidth
          />
        </Grid>


        <Grid item xs={12} sx={{alignContent: 'baseline', textAlign: 'center'}}>
          <Divider sx={{marginTop: 1, marginBottom: 2}} />

          {!!objectLoading && <CircularProgress />}
          {!objectLoading && <StyledButton
            label="Cancel"
            id='cancel'
            // contained
            onClick={(v) => {
              navigate("/internal/investments");
            }}
            sx={{marginLeft: 1}}
          />}
          {!objectLoading && <StyledButton
            label="Save"
            id='save'
            contained
            onClick={(v) => {
              mutationSave.mutate({
                formData: {
                  object: 'investment',
                  handler_id: login?.id,
                  id: objectId,
                  fields: fields,
                  ob: {
                    ...editableObject
                  }
                },
                keepOpen: false
              });
            }}
            sx={{marginLeft: 1}}
          />}
        </Grid>
      </Grid>

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
      {!!errorSnackOpen && <AlertCheckInputs open={errorSnackOpen} setOpen={(b) => {setErrorSnackOpen(b);}} />}
      {!!savedOpen && <AlertSaved open={savedOpen} setOpen={(b) => {setSavedOpen(b);}} />}
    </Page>
  );
}
